import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog9 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"


const Blog9 = () => {
  return (
     <>
     <Fullpage>
     <PageBackground>
    <BlogContainer>
      <ContentWrapper>
        <BlogHeader />
        <HeroImage
          src={blog9}
          alt="Campus website integrity illustration"
        />
        <BlogContent />
      </ContentWrapper>
    </BlogContainer>
    <OtherBlogs currentBlogId={9} />
    </PageBackground>
    </Fullpage>
    <CTASection />
  </>
  );
};

const BlogHeader = () => {
       const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <HeaderContainer>
      <BackButtonWrapper onClick={handleBackClick}>
        <BackButton />
      </BackButtonWrapper>
      <MainTitle>
        Keeping Your Campus Website Accurate, Trusted, and Effective with the Site Integrity Report
      </MainTitle>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataContent>
            <PublishDate>9 AUGUST 2025</PublishDate>
            <AuthorInfo>• POSTED BY CAMPUS INSIGHTS</AuthorInfo>
          </MetadataContent>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          In higher education, your website is often the very first interaction a prospective student—or their family—has with your institution. It's your virtual campus tour, information hub, and public face all rolled into one. But in a busy academic environment, with countless programs, events, and updates happening year-round, keeping every page accurate and up-to-date can be a real challenge. Outdated course descriptions, expired event announcements, or obsolete policy documents can linger online unnoticed, potentially damaging credibility and confusing visitors.
        </IntroParagraph>
        <HighlightParagraph>
          That's where the Site Integrity Report from Campus Insights comes in.
        </HighlightParagraph>
        <DescriptionParagraph>
          The Site Integrity Report is like a digital audit of your institution's website. It automatically scans your entire site, including linked documents, to identify outdated, inaccurate, or irrelevant content. It flags pages that reference past dates, obsolete programs, expired deadlines, or superseded documents, so you can address them before they cause confusion or diminish trust.
        </DescriptionParagraph>
        <ImpactParagraph>
          For higher education institutions, accuracy is not just about looking professional—it's about compliance, recruitment, and student success. Imagine a prospective student finding an admissions deadline from two years ago or an academic requirement that has since changed. These errors can cost you not just credibility, but potentially enrollments. Similarly, current students may rely on online resources for critical information about financial aid, course schedules, or academic policies. If these resources are out of date, it can lead to unnecessary stress and administrative workload.
        </ImpactParagraph>
      </IntroSection>

      <BlogSection title="How It Works">
        <ProcessDescription>
          The Site Integrity Report integrates seamlessly into your existing Campus Insights dashboard. Once enabled, it performs scheduled scans of your website and associated files (like PDFs, syllabi, or forms) to check for:
        </ProcessDescription>
        <FeatureList>
          <FeatureItem>• Expired dates and deadlines</FeatureItem>
          <FeatureItem>• References to discontinued programs or faculty members</FeatureItem>
          <FeatureItem>• Old branding or outdated contact details</FeatureItem>
          <FeatureItem>• Policy documents replaced by newer versions</FeatureItem>
          <FeatureItem>• Broken links or inaccessible files</FeatureItem>
        </FeatureList>
        <ResultDescription>
          It then generates a clear, prioritized list of items that need attention—along with links to the exact location on your site—so your team can make quick updates.
        </ResultDescription>
      </BlogSection>

      <BlogSection title="Benefits for Your Institution">
        <BenefitsList>
          <BenefitItem>• Save Time: No more manual page-by-page reviews; automated detection means you focus only on what needs fixing.</BenefitItem>
          <BenefitItem>• Improve Accuracy: Ensure students, faculty, and the public always see correct, current information.</BenefitItem>
          <BenefitItem>• Boost Credibility: Demonstrate professionalism and care in every online interaction</BenefitItem>
          <BenefitItem>• Support Compliance: Keep regulatory and accreditation-related content current.</BenefitItem>
          <BenefitItem>• Enhance Recruitment: Present a polished, reliable digital presence that inspires confidence.</BenefitItem>
        </BenefitsList>
      </BlogSection>

      <BlogSection title="Why It Matters">
        <ImportanceDescription>
          In a digital-first world, your website often shapes the first—and lasting—impression of your campus. The Site Integrity Report acts as your ongoing quality-control partner, ensuring your online presence remains sharp, trustworthy, and aligned with your institutional mission.
        </ImportanceDescription>
        <ConclusionStatement>
          With the Site Integrity Report, you're not just maintaining a website—you're maintaining the integrity of your institution's voice, promise, and brand.
        </ConclusionStatement>
      </BlogSection>
    </ContentContainer>
  );
};

const BlogSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <SectionContent>
        {children}
      </SectionContent>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ContentContainer = styled.main`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const IntroParagraph = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HighlightParagraph = styled.p`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const DescriptionParagraph = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ImpactParagraph = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ProcessDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ResultDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const BenefitsList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,  sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ImportanceDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ConclusionStatement = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MainTitle = styled.h1`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter,  sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PublishDate = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const AuthorInfo = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const BlogContainer = styled.article`
  display: flex;
  width: 100%;
  max-width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    padding: 40px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    padding: 24px;
    gap: 32px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  width: 100%;
`;

const HeroImage = styled.img`
  width: 100%;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 250px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

export default Blog9;
